/**
 * The skill API (Skills UX pass 2026-09-06). Every read returns the same flat
 * shape the backend's `skill_row` writes; the list asks the board once for
 * every skill's practice, which derives from the tasks and habits under it —
 * nothing is logged or scheduled on a skill itself.
 */
export type SkillLevel = 'novice' | 'beginner' | 'developing' | 'proficient' | 'advanced'

export interface Skill {
  id: number | string
  title: string
  description?: string | null
  /** The lifecycle lane, written by rationing / admission (active, parked, commitment, …). */
  status: string
  /** The stage: active · developing · ready_for_review · sufficient_for_goal · paused. Verbs set it, never a select. */
  skill_status?: string | null
  skill_status_changed_at?: string | null
  current_level?: SkillLevel | null
  target_level?: SkillLevel | null
  /** "Get there by" — a bare date. */
  target_date?: string | null
  priority: 'low' | 'medium' | 'high' | 'urgent'
  progress?: number
  life_area?: string | null
  life_areas?: string[] | null
  /** daily · most_days · few_times_a_week · weekly · now_and_then, or the person's own words. */
  practice_cadence?: string | null
  parent_goal_id?: number | null
  parent_dream_id?: number | null
  parent?: { type: 'goal' | 'dream' | 'skill' | 'aspiration'; id: number; title: string | null; life_area?: string | null } | null
  last_practice_confidence_rating?: number | null
  last_practice_confidence_at?: string | null
  created_at: string
  updated_at?: string | null
  lane?: string
  active_journey_task_id?: number | null
}

/** A task under a skill, with what its hold-me-to-it words need. */
export interface SkillTaskItem {
  id: number
  title: string
  status: string
  due_date?: string | null
  due_time?: string | null
  deadline_timezone?: string | null
  deadline_type?: string | null
  hold_lead_minutes?: number | null
  hold_interval_minutes?: number | null
  hold_quiet_hours_mode?: string | null
  target_window?: string | null
  estimated_duration?: number | null
  is_recurring?: boolean | null
  journey_step_id?: string | null
  next?: { date: string | null; time: string | null } | null
}

/** A habit under a skill, with its schedule and reminder fields and its own card. */
export interface SkillHabitItem {
  id: number
  title: string
  status?: string | null
  habit_status?: string | null
  rrule?: string | null
  scheduled_times_local?: string[] | null
  preferred_days?: string[] | null
  preferred_start_time?: string | null
  target_cadence?: string | null
  duration_minutes?: number | null
  nag_enabled?: boolean | null
  nag_window_minutes?: number | null
  nag_interval_minutes?: number | null
  nag_max_per_window?: number | null
  nag_style?: string | null
  follow_through_mode?: string | null
  timezone?: string | null
  is_active?: boolean | null
  paused_until?: string | null
  archived_at?: string | null
  live: boolean
  card: { today: { outcome: string; at: string | null; scheduled_at?: string | null } | null; next: { date: string; at: string | null } | null; streak: number } | null
}

/** A skill's card on the list page (GET /api/hierarchy/skills/board). Nothing is
 *  logged on a skill: it all derives from the tasks and habits under it. */
export interface SkillBoardCard {
  path: { plan_id: number; status: string; done: number; total: number; ongoing?: number; habit_ids?: number[]; current: { step_id: string; title: string | null; task_id: number | null } | null } | null
  /** The next thing to practise: the path's current task, the earliest open task, or the soonest habit. */
  next: { kind: 'task' | 'habit'; id: number; title: string | null; date: string | null; time: string | null; at: string | null } | null
  /** What got done under it today, the latest thing. */
  today: { kind: 'task' | 'habit'; id: number; title: string | null; at: string | null } | null
  last_14: Array<{ date: string; outcome: 'done' | null }>
  days_active: number
  window_days: number
  window_capped: boolean
  days_total: number
  last_at: string | null
  last_date: string | null
  open_tasks: number
  done_tasks: number
  items: { tasks: SkillTaskItem[]; habits: SkillHabitItem[] }
  checkpoint: { id: string; unlocks_level: string | null; current_level: string | null; target_level: string | null } | null
  words: string
}

export interface SkillBoard { today: string; skills: Record<string, SkillBoardCard> }

/** A step as sent to accept-breakdown / the path edit: a task or a habit, with a habit's schedule spec. */
export interface BreakdownStep {
  step_id?: string
  title: string
  description: string
  estimated_duration_minutes: number
  order: number
  child_type?: 'task' | 'habit'
  habit?: { rrule?: string; scheduled_times_local?: string[]; preferred_days?: string[]; target_cadence?: string; schedule_start_date?: string; duration_minutes?: number } | null
  task?: { target_window?: string; due_date?: string } | null
  /** From the engine's preview only: a suggested schedule for a habit step. */
  habit_plan?: { cadence?: string | null; days?: string[] | null; time_of_day?: string | null; minutes?: number | null } | null
}

export interface JourneyStep {
  step_id: string
  order: number
  title: string
  description: string
  estimated_duration_minutes: number
  status: 'locked' | 'unlocked' | 'completed'
  child_type?: 'task' | 'habit'
  task_id: number | null
  habit_id?: number | null
  unlocked_at: string | null
  completed_at: string | null
}

export interface JourneyPlan {
  journey_plan_id: number
  skill_id: number
  status: 'active' | 'completed' | 'archived'
  current_step_index: number
  active_task_id: number | null
  steps: JourneyStep[]
  created_at?: string
  updated_at?: string
}

export interface AcceptBreakdownResponse {
  journey_plan_id: number
  skill_id: number
  current_step_index: number
  created_task: { id: number; title: string } | null
  created_habits?: Array<{ id: number; title: string }>
  steps: Array<{ step_id: string; order?: number; title?: string; status: string; child_type?: string; task_id: number | null; habit_id?: number | null }>
}

export interface CreateSkillRequest {
  title: string
  description?: string
  priority?: 'low' | 'medium' | 'high'
  life_area?: string
  life_areas?: string[]
  current_level?: string
  target_level?: string
  target_date?: string
  practice_cadence?: string
  parent_goal_id?: number
  parent_dream_id?: number
}

export interface UpdateSkillRequest {
  title?: string
  description?: string | null
  skill_status?: string
  current_level?: string | null
  target_level?: string | null
  target_date?: string | null
  priority?: 'low' | 'medium' | 'high'
  life_area?: string | null
  life_areas?: string[]
  practice_cadence?: string | null
  parent_goal_id?: number | null
  parent_dream_id?: number | null
}

async function readJson(r: Response, fallback: string) {
  const body = await r.json().catch(() => ({}))
  if (!r.ok || body.success === false) throw new Error(body.error || fallback)
  return body
}

export class SkillService {
  private baseUrl = '/api/hierarchy/skills'

  async getSkills(includeAll = true): Promise<Skill[]> {
    // include=all: rationing parks fresh skills; an active-only read once
    // rendered this page empty while the life map showed the same items.
    const response = await fetch(`${this.baseUrl}${includeAll ? '?include=all' : ''}`, { credentials: 'include', headers: { 'Content-Type': 'application/json' } })
    if (!response.ok) throw new Error('Failed to fetch skills')
    const data = await response.json()
    return Array.isArray(data) ? data : data.data || []
  }

  async getSkill(id: string | number): Promise<Skill> {
    const response = await fetch(`${this.baseUrl}/${id}`, { credentials: 'include', headers: { 'Content-Type': 'application/json' } })
    if (!response.ok) throw new Error(`Failed to fetch skill with id ${id}`)
    const data = await response.json()
    return (data.item || data.data || data) as Skill
  }

  async getBoard(today: string): Promise<SkillBoard> {
    const r = await fetch(`${this.baseUrl}/board?today=${encodeURIComponent(today)}`, { credentials: 'include' })
    const body = await readJson(r, 'Could not load the skills board')
    return body.data as SkillBoard
  }

  async createSkill(skill: CreateSkillRequest): Promise<Skill> {
    const response = await fetch(this.baseUrl, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(skill) })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to create skill' }))
      throw new Error(error.error || 'Failed to create skill')
    }
    const data = await response.json()
    return data.data || data
  }

  async updateSkill(id: string | number, updates: UpdateSkillRequest): Promise<Skill> {
    const response = await fetch(`${this.baseUrl}/${id}`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(updates) })
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to update skill' }))
      throw new Error(error.error || 'Failed to update skill')
    }
    const data = await response.json()
    return data.data || data
  }

  async deleteSkill(id: string | number): Promise<{ cleanup: Record<string, number> }> {
    const response = await fetch(`${this.baseUrl}/${id}`, { method: 'DELETE', credentials: 'include', headers: { 'Content-Type': 'application/json' } })
    if (!response.ok) throw new Error('Failed to delete skill')
    const body = await response.json().catch(() => ({}))
    return { cleanup: (body && body.cleanup) || {} }
  }

  async previewBreakdown(skillId: number | string): Promise<{ steps: BreakdownStep[] }> {
    const response = await fetch(`${this.baseUrl}/${skillId}/preview-breakdown`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' } })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Could not map the path just now' }))
      throw new Error(err.error || 'Could not map the path just now')
    }
    return response.json()
  }

  async previewFirstStep(skillId: number | string): Promise<{ step: BreakdownStep }> {
    const response = await fetch(`${this.baseUrl}/${skillId}/preview-first-step`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' } })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Could not find a first step just now' }))
      throw new Error(err.error || 'Could not find a first step just now')
    }
    return response.json()
  }

  async acceptBreakdown(skillId: number | string, steps: BreakdownStep[]): Promise<AcceptBreakdownResponse> {
    const response = await fetch(`${this.baseUrl}/${skillId}/accept-breakdown`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ steps }) })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Could not start the path' }))
      throw new Error(err.error || 'Could not start the path')
    }
    return response.json()
  }

  async getJourneyPlan(skillId: number | string): Promise<JourneyPlan | null> {
    const response = await fetch(`${this.baseUrl}/${skillId}/journey-plan`, { credentials: 'include', headers: { 'Content-Type': 'application/json' } })
    if (response.status === 404) return null
    if (!response.ok) throw new Error('Failed to fetch journey plan')
    return response.json()
  }

  async updateJourneyPlan(skillId: number | string, steps: JourneyStep[]): Promise<JourneyPlan> {
    const response = await fetch(`${this.baseUrl}/${skillId}/journey-plan`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ steps }) })
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Could not save the path' }))
      throw new Error(err.error || 'Could not save the path')
    }
    return response.json()
  }
}
